// @ts-nocheck
/**
 * bundle-sb-controller.js
 * Simple Bundles Infinite Options integration for the bundle product page.
 *
 * Data source: simple_bundles.variant_options + simple_bundles.bundled_variants metafields.
 * Flow:
 *   1. Parse bundledVariants → build group → color → { handle, sizes, fullValues } map.
 *   2. Fetch /products/{handle}.js per unique handle → get featured image + variant availability.
 *   3. Render image swatches per slot. Auto-select first color.
 *   4. On swatch click → show size buttons, update mosaic.
 *   5. On size click → update hidden properties[optionName] input.
 *   6. ATC → POST /cart/add.js with bundle variant ID + properties, requesting the
 *      cart sections back, then dispatch Horizon's `cart:update` event so the cart
 *      drawer / cart page / cart bubble re-render without a page refresh.
 */

class BundleSBController {
  constructor(sectionEl) {
    this.section = sectionEl;

    const dataEl = document.getElementById('sb-bundle-data');
    if (!dataEl) return;
    this.data = JSON.parse(dataEl.textContent.trim());

    this.variantId        = this.data.variantId;
    this.groups           = this.data.groups || [];
    this.bundledVariants  = this.data.bundledVariants || [];
    this.threshold        = this.data.lowStockThreshold || 5;

    // Per-slot state: { color, size, fullValue }
    this.state = this.groups.map(() => ({ color: null, size: null, fullValue: null }));

    this.atcBtn       = sectionEl.querySelector('[data-bundle-atc]');
    this.atcBtnMobile = sectionEl.querySelector('[data-bundle-atc-mobile]');

    // image + product data cache keyed by handle
    this._cache = {};

    this._groupData = this._buildGroupData();
    this._run();
  }

  // ── Data construction ────────────────────────────────────────────────────────

  _buildGroupData() {
    const gd        = {};
    const imageMap  = this.data.handleImageMap  || {};
    const availMap  = this.data.availabilityMap || {};

    this.bundledVariants.forEach(bv => {
      if (!bv.variant_options || !bv.handle) return;

      bv.variant_options.forEach(vo => {
        const groupKey  = vo.name;
        const fullValue = vo.value;

        const parts = fullValue.split(' - ');
        if (parts.length < 2) return;
        const size  = parts.pop().trim();
        const color = parts.pop().trim();

        if (!gd[groupKey]) gd[groupKey] = { colorOrder: [], colors: {} };

        if (!gd[groupKey].colors[color]) {
          gd[groupKey].colors[color] = {
            handle:     bv.handle,
            image:      imageMap[bv.handle] || null,
            fullValues: {},
            sizeAvail:  {}
          };
          gd[groupKey].colorOrder.push(color);
        }

        gd[groupKey].colors[color].fullValues[size] = fullValue;
        // Use server-resolved availability; null = unknown → treated as available
        if (gd[groupKey].colors[color].sizeAvail[size] === undefined) {
          const avail = availMap[fullValue];
          gd[groupKey].colors[color].sizeAvail[size] = avail !== undefined ? avail : null;
        }
      });
    });

    return gd;
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  async _run() {
    await this._fetchAllProducts();
    this.groups.forEach((group, i) => this._renderSlot(i, group.optionName));
    this._bindATC();
    this._updateUI();
  }

  async _fetchAllProducts() {
    const seen    = new Set();
    const fetches = [];

    this.groups.forEach(group => {
      const gd = this._groupData[group.optionName];
      if (!gd) return;

      Object.entries(gd.colors).forEach(([color, cd]) => {
        if (seen.has(cd.handle)) return;
        if (cd.image) return; // already resolved via handleImageMap
        seen.add(cd.handle);

        fetches.push(
          this._fetchProduct(cd.handle).then(product => {
            if (!product) { console.warn('[BundleSBController] Failed to fetch product:', cd.handle); return; }

            // Apply image + availability to every color using this handle
            Object.values(gd.colors)
              .filter(c => c.handle === cd.handle)
              .forEach(c => {
                this._applyProductData(c, product);
              });
          })
        );
      });
    });

    await Promise.all(fetches);
  }

  _applyProductData(colorData, product) {
    const colorNorm = this._norm(
      Object.values(colorData.fullValues)[0]?.split(' - ').slice(-2, -1)[0] || ''
    );

    // Set availability and collect a color-matched variant image
    product.variants.forEach(v => {
      const opts = [v.option1, v.option2, v.option3].filter(Boolean);
      const matchesColor = opts.some(o => this._norm(o) === colorNorm);

      if (matchesColor) {
        const matchedSize = Object.keys(colorData.sizeAvail).find(s =>
          opts.some(o => this._norm(o) === this._norm(s))
        );
        if (matchedSize !== undefined) colorData.sizeAvail[matchedSize] = v.available;

        if (!colorData.image && v.featured_image?.src) {
          colorData.image = this._sizedUrl(v.featured_image.src);
        }
      }
    });

    // Fallbacks: product featured_image, then first image in images array
    if (!colorData.image) {
      if (product.featured_image) {
        colorData.image = this._sizedUrl(product.featured_image);
      } else if (Array.isArray(product.images) && product.images.length) {
        colorData.image = this._sizedUrl(product.images[0]);
      } else {
        console.warn('[BundleSBController] No image found for handle:', colorData.handle);
      }
    }
  }

  _sizedUrl(url) {
    if (!url) return null;
    const s = String(url);
    return s.includes('?') ? s + '&width=800' : s + '?width=800';
  }

  async _fetchProduct(handle) {
    if (this._cache[handle] !== undefined) return this._cache[handle];
    try {
      const res  = await fetch(`/products/${handle}.js`);
      const data = await res.json();
      this._cache[handle] = data;
      return data;
    } catch {
      this._cache[handle] = null;
      return null;
    }
  }

  // ── Rendering ─────────────────────────────────────────────────────────────────

  _renderSlot(slotIndex, groupKey) {
    const slotEl = this._slotEl(slotIndex);
    if (!slotEl) return;

    const gd = this._groupData[groupKey];
    if (!gd) return;

    const swatchContainer = slotEl.querySelector('[data-slot-swatches]');
    if (!swatchContainer) return;

    swatchContainer.innerHTML = gd.colorOrder.map((color, ci) => {
      const cd      = gd.colors[color];
      const imgHtml = cd.image
        ? `<img class="bundle-swatch__img" src="${cd.image}" alt="${this._esc(color)}" width="80" height="80" loading="lazy">`
        : `<div class="bundle-swatch__placeholder"></div>`;

      return `
        <button
          class="bundle-swatch"
          data-swatch
          data-slot="${slotIndex}"
          data-color="${this._esc(color)}"
          data-group="${this._esc(groupKey)}"
          role="listitem"
          aria-label="${this._esc(color)}"
          type="button"
        >${imgHtml}</button>`;
    }).join('');

    swatchContainer.querySelectorAll('[data-swatch]').forEach(btn => {
      btn.addEventListener('click', () => this._onSwatchClick(slotIndex, btn));
    });

    // Auto-select first
    const first = swatchContainer.querySelector('[data-swatch]');
    if (first) this._onSwatchClick(slotIndex, first);
  }

  _onSwatchClick(slotIndex, btn) {
    const slotEl   = this._slotEl(slotIndex);
    const groupKey = btn.dataset.group;
    const color    = btn.dataset.color;

    slotEl.querySelectorAll('[data-swatch]').forEach(s => s.classList.remove('is-active'));
    btn.classList.add('is-active');

    this.state[slotIndex] = { color, size: null, fullValue: null };

    const cd = this._groupData[groupKey]?.colors[color];
    if (cd?.image) this._updateMosaicTile(slotIndex, cd.image, color);

    this._renderSizes(slotIndex, groupKey, color);
    this._updateUI();
  }

  _renderSizes(slotIndex, groupKey, color) {
    const container = this.section.querySelector(`[data-slot-variants="${slotIndex}"]`);
    if (!container) return;

    const cd = this._groupData[groupKey]?.colors[color];
    if (!cd) { container.innerHTML = ''; return; }

    const sizes = Object.keys(cd.fullValues);

    container.innerHTML = `
      <div class="bundle-variants" role="group" aria-label="Select size">
        ${sizes.map(size => {
          const avail    = cd.sizeAvail[size] !== false;
          const lowStock = this.data.showLowStock && avail && typeof cd.sizeAvail[size] === 'number' && cd.sizeAvail[size] <= this.threshold && cd.sizeAvail[size] > 0;
          return `
            <div class="bundle-variant-wrap">
              <button
                class="bundle-variant-btn"
                data-variant-btn
                data-slot="${slotIndex}"
                data-size="${this._esc(size)}"
                data-group="${this._esc(groupKey)}"
                type="button"
                ${!avail ? 'disabled' : ''}
                aria-label="${this._esc(size)}${!avail ? ', sold out' : ''}"
              >${this._escHtml(size)}</button>
              ${lowStock ? `<span class="bundle-variant-btn__low-stock">Low stock</span>` : ''}
            </div>`;
        }).join('')}
      </div>`;

    container.querySelectorAll('[data-variant-btn]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this._onSizeClick(slotIndex, btn);
      });
    });
  }

  _onSizeClick(slotIndex, btn) {
    const slotEl   = this._slotEl(slotIndex);
    const groupKey = btn.dataset.group;
    const size     = btn.dataset.size;
    const color    = this.state[slotIndex].color;

    slotEl.querySelectorAll('[data-variant-btn]').forEach(b => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    const fullValue = this._groupData[groupKey]?.colors[color]?.fullValues[size] || '';
    this.state[slotIndex].size      = size;
    this.state[slotIndex].fullValue = fullValue;

    const propInput = this.section.querySelector(`[data-slot-prop="${slotIndex}"]`);
    if (propInput) propInput.value = fullValue;

    this._updateUI();
  }

  _updateMosaicTile(slotIndex, imgUrl, alt) {
    const tile = this.section.querySelector(`[data-mosaic-tile="${slotIndex}"]`);
    if (!tile) return;

    let img = tile.querySelector('.bundle-mosaic__img');
    if (!img) {
      img = document.createElement('img');
      img.className = 'bundle-mosaic__img';
      img.width     = 1200;
      img.height    = 1200;
      tile.innerHTML = '';
      tile.appendChild(img);
    }

    img.classList.add('is-loading');
    const preload   = new Image();
    preload.onload  = () => { img.src = imgUrl; img.alt = alt; img.classList.remove('is-loading'); };
    preload.src     = imgUrl;
  }

  // ── UI state ─────────────────────────────────────────────────────────────────

  _updateUI() {
    const complete = this.state.every(s => s.fullValue !== null);

    [this.atcBtn, this.atcBtnMobile].filter(Boolean).forEach(b => {
      b.disabled    = !complete;
      b.textContent = complete ? 'Add bundle to cart' : 'Select size';
    });

    this.groups.forEach((group, i) => {
      const slotEl        = this._slotEl(i);
      const statusEl      = slotEl?.querySelector('[data-slot-status]');
      const productNameEl = slotEl?.querySelector('[data-slot-product-name]');
      if (!statusEl) return;

      const s = this.state[i];

      if (s.size) {
        if (productNameEl) productNameEl.textContent = s.color;
        statusEl.textContent = s.size;
        slotEl.classList.add('is-complete');
      } else if (s.color) {
        if (productNameEl) productNameEl.textContent = s.color;
        statusEl.textContent = 'Select a size';
        slotEl.classList.remove('is-complete');
      } else {
        if (productNameEl) productNameEl.textContent = '';
        statusEl.textContent = 'Required';
        slotEl.classList.remove('is-complete');
      }
    });
  }

  // ── ATC ──────────────────────────────────────────────────────────────────────

  _bindATC() {
    [this.atcBtn, this.atcBtnMobile].filter(Boolean).forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        if (!btn.disabled) this._onAtcClick();
      });
    });
  }

  async _onAtcClick() {
    const allBtns = [this.atcBtn, this.atcBtnMobile].filter(Boolean);
    allBtns.forEach(b => { b.disabled = true; b.textContent = 'Adding…'; });

    try {
      const properties = {};
      this.groups.forEach((group, i) => {
        if (this.state[i].fullValue) {
          properties[group.optionName] = this.state[i].fullValue;
        }
      });

      // Ask the Section Rendering API for the sections that render cart contents
      // (cart drawer + cart page), exactly like Horizon's product-form-component does.
      const sectionIds = this._cartSectionIds();

      const body = { id: this.variantId, quantity: 1, properties };
      if (sectionIds.length) {
        body.sections     = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      const addUrl = this._routes().cart_add_url || '/cart/add.js';
      const res    = await fetch(addUrl, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify(body)
      });

      const payload = await res.json().catch(() => ({}));

      // Shopify returns 4xx + { status, message, description } on failure
      if (!res.ok || payload.status) {
        document.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            sourceId: 'bundle-sb',
            data: {
              message:     payload.message,
              description: payload.description,
              errors:      payload.errors
            }
          }
        }));
        throw new Error(payload.message || 'Cart add failed');
      }

      await this._refreshCart(payload.sections);

      if (this.data.cartType === 'drawer') {
        // The cart:update dispatch above auto-opens the drawer when the theme
        // setting is on; only open it manually when it isn't.
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && !drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') {
          drawer.open();
        } else if (!drawer) {
          window.location.href = '/cart';
        }
      } else {
        window.location.href = '/cart';
      }

      allBtns.forEach(b => { b.disabled = false; b.textContent = 'Add bundle to cart'; });

    } catch (err) {
      console.error('[BundleSBController] ATC error:', err);
      allBtns.forEach(b => { b.textContent = 'Error — try again'; });
      setTimeout(() => {
        allBtns.forEach(b => { b.disabled = false; b.textContent = 'Add bundle to cart'; });
      }, 2500);
    }
  }

  /**
   * Section ids that render cart contents and therefore need re-rendering after
   * an add. Mirrors Horizon's product-form-component.
   * Capped at 5 — the Section Rendering API returns 400 above that, which would
   * fail the add itself.
   * @returns {string[]}
   */
  _cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(el => {
      ids.add(el.dataset.sectionId);
    });
    return Array.from(ids).slice(0, 5);
  }

  /**
   * Brings every cart-aware component in the theme back in sync after the
   * bundle has been added.
   *
   * Horizon listens for a single event — `cart:update` (ThemeEvents.cartUpdate) —
   * on `document`. Its listeners are:
   *   - cart-items-component  → morphs the section HTML from `data.sections`,
   *                             or re-renders the section when it's missing
   *   - cart-icon             → updates the bubble from `data.itemCount`
   *   - cart-drawer-component → auto-opens the drawer
   *   - product-form / price-per-item / sticky-add-to-cart → resync from `resource`
   *
   * @param {Record<string, string>} [sections] - Rendered section HTML from /cart/add.js
   */
  async _refreshCart(sections) {
    let cart = null;

    try {
      const cartUrl = `${this._routes().cart_url || '/cart'}.js`;
      const cartRes = await fetch(cartUrl, {
        headers: { 'Accept': 'application/json' },
        cache:   'no-store'
      });
      cart = await cartRes.json();
    } catch (err) {
      console.warn('[BundleSBController] Could not fetch cart:', err);
    }

    // `itemCount` is absolute here (not a delta), so `source` must NOT be
    // 'product-form-component' — cart-icon adds to the current count for that source.
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'bundle-sb',
        data: {
          source:    'bundle-sb-controller',
          itemCount: cart ? cart.item_count : 0,
          productId: this.data.productId,
          variantId: String(this.variantId),
          sections
        }
      }
    }));

    // Legacy/app-facing events — kept for any third-party listeners.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  /**
   * `Theme` from snippets/scripts.liquid is a script-scoped const, not a
   * window property, so it has to be read as a bare identifier.
   * @returns {Record<string, string>}
   */
  _routes() {
    return (typeof Theme !== 'undefined' && Theme.routes) ? Theme.routes : {};
  }

  _slotEl(index) {
    return this.section.querySelector(`[data-slot-index="${index}"]`);
  }

  _norm(str) {
    return String(str || '').trim().toLowerCase()
      .replace(/['''`""‘’“”]/g, '')
      .replace(/\s+/g, ' ');
  }

  _esc(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  _escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

function init() {
  document.querySelectorAll('[data-bundle-sb-section]').forEach(el => new BundleSBController(el));
}
